import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { ToggleButtonOption } from "../components/form/APFormToggleGroup";
import { GstAmountValues, PeriodicAmounts } from "./Calculation";
import { FormattedCurrencyValue, FormattedValue } from "./FormattedValues";
import { State } from "./State";
import { APVehicleVariant, APVehicleVariantLabelMap } from "./Vehicles";

export type DepreciationMethod = "prime-cost" | "diminishing-value";
export type LuxuryCarChargeMethod = "standard" | "employee-contribution-offset";

export const depreciationMethodLabels: Record<DepreciationMethod, string> = {
  "prime-cost": "Prime Cost",
  "diminishing-value": "Diminishing Value",
};

export const luxuryChargeMethodLabels: Record<LuxuryCarChargeMethod, string> = {
  standard: "Basic",
  "employee-contribution-offset": "Employee Contribution Offset",
};

export type DossierQuoteSyncStatus =
  | "NOT_SYNCING"
  | "SYNCING"
  | "ON_COOLDOWN"
  | "SUCCESS"
  | "FAILED";
export type DossierQuoteSyncInitResult =
  | "NO_ACTIVE_DOSSIER"
  | "CATCHE_DRIVER_NOT_LINKED"
  | "SYNCING";

export type DossierTransactionSyncStatus =
  | "NOT_SYNCING"
  | "SYNCING"
  | "SUCCESS"
  | "FAILED";

export type QuoteCalculationStatus = "valid" | "invalid" | "valid-override";

// Quote
export type QuoteIdentifiers = {
  crmQuoteId: string;
  crmContactId: string;
  crmDossierId: string;
  crmOpportunityId: string;
  catchEQuoteId: string;
  catchEQuoteUrl: string;
  name: string;
};

export type QuoteTerm = {
  startDate: string | null;
  endDate: string | null;
  termMonths: number;
  termYears: number;
  payPeriod: QuotePayPeriod;
};

export type QuoteConfig = {
  updatedAt: string;
  isLocked: boolean;
  isVehicleConfigurationLocked: boolean;
  isVisible: boolean;
  calculatedAt: string | null;
  lockCalculatedAt: boolean;
  depreciationMethod: DepreciationMethod | null;
  luxuryCarChargeMethod: LuxuryCarChargeMethod | null;
  overrideEmployerLuxuryLeaseConfig: boolean;
  maxFinance: number | null;
  calculationStatus: QuoteCalculationStatus | null;
};

export type QuoteVehicleInfo = {
  make: string;
  makeId: string;
  model: string;
  modelId: string;
  year: string;
  variantName: string;
  variantId: string;
  variantCode: string;
  primaryImageUrl: string | null;
  initialOdometer: number | null;
  residual: GstAmountValues;
  onroadCosts: number;
  listPrice: GstAmountValues;
  discount: number | null;
  state: State;
  budgetedAnnualKm: number;
  purchaseType: VehiclePurchaseType;
  businessUsePercentage: number;
};

export type QuoteSalaryComparison = {
  salaryExSuper: number;
  runningCosts: PeriodicAmounts<number>;
  netCost: PeriodicAmounts<number>;
  grossCost: PeriodicAmounts<number>;
  savings: PeriodicAmounts<number>;
  savingsPercentage: number;
};

export type QuoteRates = {
  financeInterestRate: number | null;
  brokerageRate: number | null;
  netFinanceInterestRate: number | null;
  netBrokerageRate: number | null;
};

export type NetQuoteInclusions = {
  finance: PeriodicAmounts<number>;
  servicing: PeriodicAmounts<number>;
  tyres: PeriodicAmounts<number>;
  fuel: PeriodicAmounts<number>;
  rego: PeriodicAmounts<number>;
  insurance: PeriodicAmounts<number>;
  rsa: PeriodicAmounts<number>;
  managementFees: PeriodicAmounts<number>;
  luxuryCharge: PeriodicAmounts<number>;
  gst: PeriodicAmounts<number>;
};

export type QuotePreferences = {
  exteriorPaintColourPreference: string | null;
  interiorPaintColourPreference: string | null;
};

export type Quote = {
  identifiers: QuoteIdentifiers;
  term: QuoteTerm;
  vehicle: QuoteVehicleInfo;
  inclusions: NetQuoteInclusions;
  salaryComparison: QuoteSalaryComparison;
  rates: QuoteRates;
  config: QuoteConfig;
  preferences: QuotePreferences;
};

export type QuoteWithVehicleSpecification = Quote & {
  vehicle: QuoteVehicleInfo & {
    specification: APVehicleVariant | null;
  };
};

export type PersonalisedQuoteVehicleInfo = {
  series: string;
  body: string;
  doors: number;
  seats: number;
  transmission: string;
  driveType: string;
  fuelType: string;
  powerPlantType: string;
  co2Emissions: number;
  ancapRating: number;
  serviceKms: number;
  standardEquipment: string[];
  optionalEquipment: string[];
  catchEEquipmentIds: string[];
  catchEInclusionSellPrices: Record<string, GstAmountValues>;
};

// List Quote
export type ListQuoteVehicleInfo = Pick<
  QuoteVehicleInfo,
  | "make"
  | "model"
  | "year"
  | "variantName"
  | "variantCode"
  | "variantId"
  | "primaryImageUrl"
  | "onroadCosts"
  | "budgetedAnnualKm"
  | "purchaseType"
>;

export type ListQuoteSalaryComparison = Pick<
  QuoteSalaryComparison,
  "netCost" | "savingsPercentage"
>;

export type ListQuote = Omit<
  Quote,
  "vehicle" | "salaryComparison" | "inclusions" | "rates"
> & {
  vehicle: ListQuoteVehicleInfo;
  salaryComparison: ListQuoteSalaryComparison;
};

export const payCyclePeriodOptions: ToggleButtonOption[] = [
  { label: "Week", id: "weekly" },
  { label: "Fortnight", id: "fortnightly" },
  { label: "Month", id: "monthly" },
  { label: "Year", id: "annual" },
];

export type QuotePayPeriod = "weekly" | "fortnightly" | "monthly" | "annual";
export type VehiclePurchaseType =
  | "new"
  | "renovation"
  | "refinance"
  | "sale-and-leaseback"
  | "used"
  | "external-financier-takeover";

export const vehiclePurchaseTypeOptions: AutoCompleteOption[] = [
  { label: "New", id: "new" },
  { label: "Used", id: "used" },
  { label: "Lease my own car", id: "sale-and-leaseback" },
];

export const internalOnlyPurchaseTypeOptions: AutoCompleteOption[] = [
  { label: "Renovation", id: "renovation" },
  { label: "Refinance", id: "refinance" },
  { label: "External Financier Takeover", id: "external-financier-takeover" },
];

export type QuoteInclusionsFormatted = {
  finance: FormattedCurrencyValue;
  servicing: FormattedCurrencyValue;
  tyres: FormattedCurrencyValue;
  fuel: FormattedCurrencyValue;
  rego: FormattedCurrencyValue;
  insurance: FormattedCurrencyValue;
  managementFees: FormattedCurrencyValue;
  luxuryCharge: FormattedCurrencyValue;
  gst: FormattedCurrencyValue;
};

export type QuoteFormatted = {
  id: string;
  dossierId: string;
  opportunityId: string;
  name: string;
  isLocked: boolean;
  isVehicleConfigurationLocked: boolean;
  vehicle: {
    make: string;
    makeId: string;
    model: string;
    modelId: string;
    year: string;
    description: string;
    variantId: string;
    variantCode: string;
    initialOdometer: FormattedValue<number> | null;
  };
  state: string;
  packageValue: FormattedCurrencyValue;
  netCost: FormattedCurrencyValue;
  inclusions: QuoteInclusionsFormatted;
  payPeriod: FormattedValue<QuotePayPeriod>;
  runningCostsPA: FormattedCurrencyValue;
  annualSalary: FormattedCurrencyValue;
  annualKm: FormattedValue<number>;
  vehicleOnroadCosts: FormattedCurrencyValue;
  vehicleResidualGross: FormattedCurrencyValue;
  term: FormattedValue<number>;
  businessUsage: FormattedValue<number>;
  savings: FormattedValue<number>;
  savingsPerYear: FormattedCurrencyValue;
  lifeOfLeaseSavings: FormattedCurrencyValue;
  listPrice: FormattedCurrencyValue;
  catchEQuoteId: string;
  catchEQuoteUrl: string;
  purchaseType: FormattedValue<VehiclePurchaseType>;
  discount: FormattedCurrencyValue | null;
  financeInterestRate: FormattedValue<number> | null;
  brokerageRate: FormattedValue<number> | null;
  netFinanceInterestRate: FormattedValue<number> | null;
  netBrokerageRate: FormattedValue<number> | null;
};

export type QuoteComparisonItem = {
  quoteId: string;
  isLocked: boolean;
  name: string;
  make: string;
  model: string;
  year: string;
  imageUrl: string;
  savingsPercentage: number;
  weeklyNetCost: number;
  summary: string;
  annualSavings: number;
  "quote.annualKm": number;
  "quote.term": number;
  "quote.businessUsage": number;
  "quote.residual": number;
  "vehicle.name": string;
  "vehicle.onRoadCost": number;
  "vehicle.powerPlant": string | null;
  "vehicle.warrantYrs": number | null;
  "vehicle.powerKw": number | null;
  "vehicle.torque": number | null;
  "vehicle.seats": number | null;
  "vehicle.acceleration": number | null;
  "vehicle.cargoVolume": number | null;
  "vehicle.fuelEconomy": number | null;
  "vehicle.range": number | null;
  "vehicle.ancapRating": number | null;
  primary: boolean;
};

export const selectableQuoteComparisonCriteria: (keyof QuoteComparisonItem)[] =
  [
    "vehicle.onRoadCost",
    "vehicle.warrantYrs",
    "savingsPercentage",
    "weeklyNetCost",
    "annualSavings",
    "vehicle.powerPlant",
    "vehicle.powerKw",
    "vehicle.torque",
    "vehicle.seats",
    "vehicle.acceleration",
    "vehicle.cargoVolume",
    "vehicle.fuelEconomy",
    "vehicle.range",
  ];

export const CriteriaLabelMap: Record<keyof QuoteComparisonItem, string> = {
  quoteId: "",
  isLocked: "",
  name: "",
  make: "",
  model: "",
  year: "",
  imageUrl: "",
  savingsPercentage: "Savings %",
  weeklyNetCost: "Weekly Net Cost",
  summary: "Summary",
  annualSavings: "Annual Savings",
  "quote.annualKm": "Annual Km",
  "quote.term": "Term",
  "quote.businessUsage": "Business Usage",
  "quote.residual": "Residual",
  "vehicle.name": APVehicleVariantLabelMap.make,
  "vehicle.onRoadCost": "On Road Cost",
  "vehicle.powerPlant": APVehicleVariantLabelMap.powerPlantType,
  "vehicle.warrantYrs": APVehicleVariantLabelMap.warrantyYears,
  "vehicle.powerKw": APVehicleVariantLabelMap.powerKw,
  "vehicle.torque": APVehicleVariantLabelMap.torque,
  "vehicle.seats": APVehicleVariantLabelMap.seatCapacity,
  "vehicle.acceleration": APVehicleVariantLabelMap.acceleration,
  "vehicle.cargoVolume": APVehicleVariantLabelMap.minCargoVolume,
  "vehicle.fuelEconomy": APVehicleVariantLabelMap.fuelEconomy,
  "vehicle.range": APVehicleVariantLabelMap.range,
  "vehicle.ancapRating": APVehicleVariantLabelMap.ancaprating,
  primary: "Favourite",
};

export const PeroidLabelMap: Record<QuotePayPeriod, string> = {
  weekly: "week",
  fortnightly: "fortnight",
  monthly: "month",
  annual: "year",
};
